import { useCallback, useEffect, useRef, useState } from 'react';
import { getAddressById, getHouseNumberById, searchAddresses, searchHousesOnStreet } from '../../../../api';
import { municipalities } from './municipalities';
import type { AdresseVaelgerFund, FslAddress } from './AddressPicker.types';

export const useData = (onChange?: (changes: Partial<FslAddress>) => void) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<AdresseVaelgerFund[]>([]);
  const [selectedOption, setSelectedOption] = useState<AdresseVaelgerFund | undefined>();
  const [isOpen, setIsOpen] = useState(false);
  const lookupIdRef = useRef(0);

  useEffect(() => {
    if (!query.trim()) {
      setOptions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      const newOptions = await searchAddresses(query);
      if (!cancelled) {
        setOptions(newOptions);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const handleOnChange = useCallback(
    (_: unknown, value: AdresseVaelgerFund | null) => {
      if (!value?.id) {
        return;
      }
      const { id, type } = value;

      if (type === 'navngivenvejpostnummer') {
        setSelectedOption(value);
        const lookupId = ++lookupIdRef.current;
        searchHousesOnStreet(value.vejnavn ?? '', value.postnr ?? '').then((houses) => {
          if (lookupId !== lookupIdRef.current || !houses.length) {
            return;
          }
          setOptions(houses);
          setSelectedOption(undefined);
        });
        return;
      }

      setIsOpen(false);
      setSelectedOption(value);

      const lookupId = ++lookupIdRef.current;
      const lookup = type === 'husnummer' ? getHouseNumberById(id) : getAddressById(id);
      lookup.then((result) => {
        if (lookupId !== lookupIdRef.current || !result) {
          return;
        }
        const husnummer = type === 'husnummer' ? result : result.husnummer;
        const etage = type === 'adresse' && result.etagebetegnelse ? `, ${result.etagebetegnelse}` : '';
        const doer = type === 'adresse' && result.doerbetegnelse ? `, ${result.doerbetegnelse}` : '';
        onChange?.({
          address: `${husnummer.vejnavn} ${husnummer.husnummertekst}${etage}${doer}`,
          postalCode: husnummer.postnummer.postnr,
          city: husnummer.postnummer.navn,
          countryCode: 'DK',
          municipality: municipalities[husnummer.navngivenvejkommunedel.kommune] ?? '',
        });
      });
    },
    [onChange, setIsOpen, setSelectedOption, setOptions]
  );

  const handleOnInputChanged = useCallback((_: unknown, newValue: string) => {
    setQuery(newValue);
    setSelectedOption({ type: 'adresse', id: undefined, titel: newValue });
  }, []);

  return { query, options, isOpen, setIsOpen, handleOnChange, handleOnInputChanged, selectedOption };
};
